import React from "react";
import { BaseModal } from "@src/common/components";
import { useShipmentSelectionContext } from "../../context/ShipmentSelectionContext";
import { useActionPaletteSearch } from "./hooks/useActionPaletteSearch";
import { useActionPaletteKeyboard } from "./hooks/useActionPaletteKeyboard";
import { useRunPaletteAction } from "./hooks/useRunPaletteAction";
import { PaletteSearchInput } from "./components/PaletteSearchInput";
import { PaletteActionList } from "./components/PaletteActionList";
import { SHORTCUT_DISPLAY } from "@src/common/components/ShipmentsTableDropDown";

// Spark-style command palette: opens on right-clicking a row, lets you search
// the row's available actions and run one via keyboard or click. The pieces
// live in ./hooks (search, keyboard nav, run) and ./components (UI).
export function ShipmentRowActionPalette() {
  const { selectedRow, isActionPaletteOpen, setIsActionPaletteOpen } =
    useShipmentSelectionContext();

  const isOpen = isActionPaletteOpen && !!selectedRow;
  const close = () => setIsActionPaletteOpen(false);

  const { query, setQuery, filteredActions, destructiveKeys } =
    useActionPaletteSearch(selectedRow, isActionPaletteOpen);
  const runAction = useRunPaletteAction(selectedRow, close);
  const { highlightedIndex, setHighlightedIndex, itemRefs, handleKeyDown } =
    useActionPaletteKeyboard({
      itemCount: filteredActions.length,
      isOpen: isActionPaletteOpen,
      resetKey: selectedRow,
      onEnter: (index) => {
        const item = filteredActions[index];
        if (item) runAction(item);
      },
      onClose: close,
    });

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={close}
      overrides={{
        Root: {
          style: {
            zIndex: 2000,
          },
        },
        Dialog: {
          style: {
            width: "480px",
            maxWidth: "90vw",
            borderTopLeftRadius: "12px",
            borderTopRightRadius: "12px",
            borderBottomLeftRadius: "12px",
            borderBottomRightRadius: "12px",
          },
          props: {
            className: "dark:bg-gray-800",
          },
        },
      }}
    >
      <div onKeyDown={handleKeyDown} className="dark:bg-gray-800">
        <div className="px-4 py-3 border-b dark:border-gray-700">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Order {selectedRow?.orderDisplayId}
          </span>
        </div>
        <PaletteSearchInput query={query} setQuery={setQuery} />
        <PaletteActionList
          actions={filteredActions}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          itemRefs={itemRefs}
          onSelect={runAction}
          getShortcut={(key) => SHORTCUT_DISPLAY[key]}
          isDestructive={(item) => destructiveKeys.has(item.key)}
        />
      </div>
    </BaseModal>
  );
}

export default ShipmentRowActionPalette;
